import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TrustedBy from './common/TrustedBy';
import styles from './Home.module.scss';

// ============================================================
// RANDOM HERO AMBIENT VIDEOS (Optimized Cloudinary Edge Stream)
// ============================================================
const HERO_VIDEOS_CLOUDINARY = [
  {
    src: 'https://res.cloudinary.com/dbktbwwho/video/upload/f_mp4,vc_h264,q_auto:eco,w_1280,c_limit,ac_none/foth/hero/hero-video-1.mp4',
    poster: 'https://res.cloudinary.com/dbktbwwho/video/upload/so_0,w_1280,f_jpg,q_auto/foth/hero/hero-video-1.jpg',
  },
  {
    src: 'https://res.cloudinary.com/dbktbwwho/video/upload/f_mp4,vc_h264,q_auto:eco,w_1280,c_limit,ac_none/foth/hero/hero-video-2.mp4',
    poster: 'https://res.cloudinary.com/dbktbwwho/video/upload/so_0,w_1280,f_jpg,q_auto/foth/hero/hero-video-2.jpg',
  },
  {
    src: 'https://res.cloudinary.com/dbktbwwho/video/upload/f_mp4,vc_h264,q_auto:eco,w_1280,c_limit,ac_none/foth/hero/hero-video-3.mp4',
    poster: 'https://res.cloudinary.com/dbktbwwho/video/upload/so_0,w_1280,f_jpg,q_auto/foth/hero/hero-video-3.jpg',
  },
];

// ============================================================
// INDUSTRY SHOWCASE DATA & COMPONENT
// ============================================================
const IndustryShowcase = () => {
  const { t } = useTranslation('home');
  const [active, setActive] = useState(0);

  const industries = [
    {
      id: 'restaurants',
      label: t('industries.list.restaurants.label'),
      image: '/assets/images/industries/gastronomy.png',
      stat1: { value: t('industries.curation'), label: t('industries.list.restaurants.stat1_label') },
      stat2: { value: t('industries.aligned'), label: t('industries.list.restaurants.stat2_label') },
      tag: t('industries.tag'),
      mockLocation: 'Panamá / Casco Viejo',
      mockDeliverables: '1 Reel + 2 Historias',
      mockTime: 'Campañas de 30 Días',
      desc: t('industries.list.restaurants.desc'),
    },
    {
      id: 'hotels',
      label: t('industries.list.hotels.label'),
      image: '/assets/images/industries/hotels.png',
      stat1: { value: t('industries.curation'), label: t('industries.list.hotels.stat1_label') },
      stat2: { value: t('industries.aligned'), label: t('industries.list.hotels.stat2_label') },
      tag: t('industries.tag'),
      mockLocation: 'Tulum / Riviera Maya',
      mockDeliverables: '1 TikTok + 3 Fotos',
      mockTime: 'Estancias de 3 Días',
      desc: t('industries.list.hotels.desc'),
    },
    {
      id: 'wellness',
      label: t('industries.list.wellness.label'),
      image: '/assets/images/industries/wellness.png',
      stat1: { value: t('industries.curation'), label: t('industries.list.wellness.stat1_label') },
      stat2: { value: t('industries.aligned'), label: t('industries.list.wellness.stat2_label') },
      tag: t('industries.tag'),
      mockLocation: 'Global / Envíos',
      mockDeliverables: 'Unboxing + Reseña',
      mockTime: 'Entregables quincenales',
      desc: t('industries.list.wellness.desc'),
    },
    {
      id: 'events',
      label: t('industries.list.events.label'),
      image: '/assets/images/industries/events.png',
      stat1: { value: t('industries.curation'), label: t('industries.list.events.stat1_label') },
      stat2: { value: t('industries.aligned'), label: t('industries.list.events.stat2_label') },
      tag: t('industries.tag'),
      mockLocation: 'Bogotá / Zona T',
      mockDeliverables: 'Cobertura en vivo + 1 Post',
      mockTime: 'Evento 1 Noche',
      desc: t('industries.list.events.desc'),
    },
    {
      id: 'lifestyle',
      label: t('industries.list.lifestyle.label'),
      image: '/assets/images/industries/lifestyle.png',
      stat1: { value: t('industries.curation'), label: t('industries.list.lifestyle.stat1_label') },
      stat2: { value: t('industries.aligned'), label: t('industries.list.lifestyle.stat2_label') },
      tag: t('industries.tag'),
      mockLocation: 'Miami / Design District',
      mockDeliverables: 'Lookbook + 3 Reels',
      mockTime: 'Campaña de Temp. Alta',
      desc: t('industries.list.lifestyle.desc'),
    },
  ];

  const current = industries[active];

  return (
    <div className={styles.industryShowcase}>
      {/* Left: category selector */}
      <div className={styles.industryTabs}>
        <div className={styles.industryTabsLabel}>{t('industries.label')}</div>
        {industries.map((ind, idx) => (
          <button
            key={ind.id}
            className={clsx(styles.industryTab, active === idx && styles.industryTabActive)}
            onClick={() => setActive(idx)}
          >
            <span>{ind.label}</span>
            {active === idx && (
              <motion.div
                layoutId="activeIndicator"
                className={styles.industryTabIndicator}
              />
            )}
          </button>
        ))}
      </div>

      {/* Right: image panel */}
      <div className={styles.industryImagePanel}>
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            className={styles.industryImageWrap}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={current.image} alt={current.label} />
            <div className={styles.industryImageOverlay} />

            {/* Floating Mock Collab Data for realistic feel */}
            <motion.div
              className={styles.industryMockBadge}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className={styles.mockBadgeRow}>{current.mockLocation}</div>
              <div className={styles.mockBadgeRow}>{current.mockDeliverables}</div>
              <div className={styles.mockBadgeRow}>{current.mockTime}</div>
            </motion.div>

            {/* Floating tag */}
            <motion.div
              className={styles.industryFloatTag}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              {current.tag}
            </motion.div>

            {/* Bottom info */}
            <motion.div
              className={styles.industryInfo}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className={styles.industryInfoTitle}>{current.label}</div>
              <p className={styles.industryInfoDesc}>{current.desc}</p>
              <div className={styles.industryStats}>
                <div className={styles.industryStat}>
                  <div className={styles.industryStatValue}>{current.stat1.value}</div>
                  <div className={styles.industryStatLabel}>{current.stat1.label}</div>
                </div>
                <div className={styles.industryStatDivider} />
                <div className={styles.industryStat}>
                  <div className={styles.industryStatValue}>{current.stat2.value}</div>
                  <div className={styles.industryStatLabel}>{current.stat2.label}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ============================================================
// PROBLEM VS SOLUTION
// ============================================================
const ProblemVsSolution = () => {
  const { t } = useTranslation('home');

  const chaosItems = t('pvs.chaos.items', { returnObjects: true });
  const fothItems = t('pvs.foth.items', { returnObjects: true });

  return (
    <div className={styles.pvsGrid}>
      <div className={styles.pvsPanel}>
        <div className={styles.pvsPanelLabel} data-type="chaos">
          {t('pvs.chaos.label')}
        </div>
        <ul className={styles.pvsList}>
          {chaosItems.map((item) => (
            <li key={item} className={styles.pvsItemChaos}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.pvsDivider}>
        <div className={styles.pvsDividerLine} />
        <div className={styles.pvsDividerBadge}>vs</div>
        <div className={styles.pvsDividerLine} />
      </div>
      <div className={clsx(styles.pvsPanel, styles.pvsPanelFoth)}>
        <div className={styles.pvsPanelLabel} data-type="foth">
          {t('pvs.foth.label')}
        </div>
        <ul className={styles.pvsList}>
          {fothItems.map((item) => (
            <li key={item} className={styles.pvsItemFoth}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ============================================================
// HOW IT WORKS — Responsive 4-Step Architecture
// ============================================================
const HowItWorks = () => {
  const { t } = useTranslation('home');

  const steps = [
    {
      num: '01',
      title: t('how.steps.0.title'),
      desc: t('how.steps.0.desc'),
    },
    {
      num: '02',
      title: t('how.steps.1.title'),
      desc: t('how.steps.1.desc'),
    },
    {
      num: '03',
      title: t('how.steps.2.title'),
      desc: t('how.steps.2.desc'),
    },
    {
      num: '04',
      title: t('how.cta.title'),
      desc: t('how.cta.desc'),
      isCta: true,
      ctaText: t('how.cta.button'),
    },
  ];

  return (
    <section id="como-funciona" className={styles.howSection}>
      <div className={styles.container}>
        <div className={styles.howHeader}>
          <div className={styles.howEyebrow}>{t('how.eyebrow')}</div>
          <h2 className={styles.howSectionTitle}>La Arquitectura de la Colaboración</h2>
        </div>

        <div className={styles.howStepsGrid}>
          {steps.map((step) => (
            <div
              key={step.num}
              className={clsx(styles.howStepCard, step.isCta && styles.howStepCta)}
            >
              <div className={styles.howCardNumLarge}>{step.num}</div>
              <div>
                <h3 className={styles.howCardTitle}>{step.title}</h3>
                <p className={styles.howCardDesc}>{step.desc}</p>
                {step.isCta && (
                  <Link to="/register" className={styles.howCardCtaBtn}>
                    {step.ctaText}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// MAIN HOMEPAGE COMPONENT
// ============================================================
const Home = () => {
  const { t } = useTranslation('home');
  const [isLoading, setIsLoading] = useState(true);
  const [heroItem, setHeroItem] = useState(HERO_VIDEOS_CLOUDINARY[0]);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  // Select a random hero video on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    const randomIndex = Math.floor(Math.random() * HERO_VIDEOS_CLOUDINARY.length);
    setHeroItem(HERO_VIDEOS_CLOUDINARY[randomIndex]);
    return () => clearTimeout(timer);
  }, []);

  // Programmatic muted autoplay policy compliance for Chrome, Safari and mobile
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !heroItem?.src) return;

    // Direct DOM property enforcement required by WebKit & modern browser autoplay policies
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;

    const startPlayback = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsVideoPlaying(true))
          .catch((err) => {
            console.warn('[FOTH] Autoplay held by browser, retrying muted:', err);
            video.muted = true;
            video.play().then(() => setIsVideoPlaying(true)).catch(() => {});
          });
      }
    };

    startPlayback();
  }, [heroItem]);

  if (isLoading) {
    return (
      <div className={styles.loadingScreen}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={styles.loadingLogo}
        >
          {t('loading')}
        </motion.div>
      </div>
    );
  }

  const heroTitleParts = (t('hero.title') || '').split('\n');

  return (
    <div className={styles.homePage}>
      {/* ====== HERO — Full-Width Editorial ====== */}
      <section
        className={styles.heroSection}
        style={{
          backgroundImage: `url(${heroItem.poster})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {heroItem?.src && (
          <video
            ref={videoRef}
            className={clsx(styles.heroVideoBg, isVideoPlaying && styles.heroVideoPlaying)}
            src={heroItem.src}
            poster={heroItem.poster}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onCanPlay={() => setIsVideoPlaying(true)}
            onPlaying={() => setIsVideoPlaying(true)}
            onError={() => {
              console.warn('[FOTH] Video stream fallback triggered');
              if (heroItem.src !== HERO_VIDEOS_CLOUDINARY[0].src) {
                setHeroItem(HERO_VIDEOS_CLOUDINARY[0]);
              }
            }}
          />
        )}
        <div className={styles.heroOverlay} aria-hidden="true" />

        <div className={styles.container}>
          <motion.div
            className={styles.heroContent}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Eyebrow */}
            <motion.div
              className={styles.heroBadge}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              {t('hero.badge')}
            </motion.div>

            {/* Headline */}
            <h1 className={styles.heroTitle}>
              {heroTitleParts[0]}
              {heroTitleParts[1] && (
                <>
                  <br />
                  <em>{heroTitleParts[1]}</em>
                </>
              )}
            </h1>

            {/* Subtext */}
            <p className={styles.heroSubtext}>
              {t('hero.subtitle')}
            </p>

            {/* Gold accent divider */}
            <div className={styles.heroDivider} aria-hidden="true" />

            {/* CTAs */}
            <div className={styles.heroCtas}>
              <Link
                to="/register"
                id="hero-cta-enter"
                className={styles.heroCtaPrimary}
              >
                {t('hero.cta.apply')}
              </Link>
              <a
                href="#que-es-foth"
                id="hero-cta-explore"
                className={styles.heroCtaSecondary}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('que-es-foth')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('hero.cta.explore')}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className={styles.heroScrollIndicator}
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
        >
          <div className={styles.heroScrollLine} />
        </motion.div>
      </section>

      {/* ====== QUÉ ES FOTH — Definition strip ====== */}
      <section id="que-es-foth" className={styles.whatSection}>
        <div className={styles.container}>
          <motion.div
            className={styles.whatCard}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={styles.whatEyebrow}>{t('what.eyebrow')}</div>
            <div className={styles.whatBodyGroup}>
              <p className={styles.whatBodyLead}>
                {t('what.lead')}
              </p>
              <p className={styles.whatBodySecondary}>
                {t('what.secondary')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====== PROOF GRID STANDALONE ====== */}
      <section className={styles.standaloneProofSection}>
        <div className={styles.container}>
          <div className={styles.standaloneProofHeader}>
            <div className={styles.standaloneProofEyebrow}>{t('proof.eyebrow')}</div>
            <h2 className={styles.standaloneProofTitle}>{t('proof.title')}</h2>
          </div>
          <div className={styles.standaloneProofGrid}>
            {t('proof.items', { returnObjects: true }).map((point) => (
              <div key={point.label} className={styles.standaloneProofCard}>
                <div className={styles.standaloneProofValue}>{point.value}</div>
                <div className={styles.standaloneProofLabel}>{point.label}</div>
                <div className={styles.standaloneProofSource}>{point.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== WHY FOTH ====== */}
      <section id="why-foth" className={styles.whyFothSection}>
        <div className={styles.container}>
          <div className={styles.whyFothCard}>
            <div className={styles.whyFothHeader}>
              <div className={styles.whyFothEyebrow}>{t('why.eyebrow')}</div>
              <h2 className={styles.whyFothTitle}>{t('why.title')}</h2>
            </div>
            <div className={styles.whyFothDesc}>
              {t('why.desc')}
            </div>
          </div>
        </div>
      </section>

      {/* ====== CÓMO FUNCIONA — Responsive 4-Step Reveal ====== */}
      <HowItWorks />

      {/* ====== PROBLEM VS SOLUTION ====== */}
      <section id="el-estandar" className={styles.pvsSection}>
        <div className={styles.container}>
          <div className={styles.pvsSectionHeader}>
            <div className={styles.pvsEyebrow}>{t('pvs.eyebrow')}</div>
            <h2 className={styles.pvsSectionTitle}>
              {t('pvs.title')}
            </h2>
            <p className={styles.pvsSectionDesc}>
              {t('pvs.desc')}
            </p>
          </div>
          <ProblemVsSolution />
        </div>
      </section>

      {/* ====== INDUSTRY SHOWCASE ====== */}
      <section className={styles.industrySection}>
        <div className={styles.container}>
          <div className={styles.industrySectionHeader}>
            <div className={styles.industryEyebrow}>{t('industries.eyebrow')}</div>
            <h2 className={styles.industrySectionTitle}>
              {t('industries.title')}
            </h2>
          </div>
          <IndustryShowcase />
        </div>
      </section>

      {/* ====== PRODUCT CARDS ====== */}
      <section className={styles.productSection}>
        <div className={styles.container}>
          <div className={styles.productGrid}>
            {[
              { 
                title: t('products.card1.title'), 
                text: t('products.card1.desc'),
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )
              },
              { 
                title: t('products.card2.title'), 
                text: t('products.card2.desc'),
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 6v6h6l-2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7l-2-2h6V6a3 3 0 0 1 6 0Z"></path>
                  </svg>
                )
              },
              { 
                title: t('products.card3.title'), 
                text: t('products.card3.desc'),
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h12l4 6-10 12L2 9Z"></path>
                    <path d="M11 3 8 9l4 12 4-12-3-6"></path>
                    <path d="M2 9h20"></path>
                  </svg>
                )
              }
            ].map(card => (
              <div key={card.title} className={styles.productCard}>
                <div className={styles.productIconWrap}>
                  {card.icon}
                </div>
                <div className={styles.productTitle}>{card.title}</div>
                <p className={styles.productDesc}>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CREATOR GALLERY ====== */}
      <section className={styles.gallerySection}>
        <div className={styles.container}>
          <div className={styles.gallerySectionHeader}>
            <div className={styles.galleryEyebrow}>{t('gallery.eyebrow')}</div>
            <h2 className={styles.galleryTitle}>
              {t('gallery.title').split('\n')[0]}<br />
              {t('gallery.title').split('\n')[1]}
            </h2>
            <p className={styles.gallerySubtitle}>
              {t('gallery.subtitle')}
            </p>
          </div>

          <div className={styles.galleryGrid}>
            {/* Photo 1 — tall */}
            <div className={styles.galleryItem} data-span="tall">
              <img src="/assets/images/gallery/creator_1.png" alt="Creator en espacio arquitectónico minimalista" />
              <div className={styles.galleryOverlay}>
                <span className={styles.galleryTag}>{t('gallery.tags.curated')}</span>
              </div>
            </div>

            {/* Right col: 2 photos stacked */}
            <div className={styles.galleryColStack}>
              <div className={styles.galleryItem}>
                <img src="/assets/images/gallery/creator_2.png" alt="Estudio de moda editorial de alto nivel" />
                <div className={styles.galleryOverlay}>
                  <span className={styles.galleryTag}>{t('gallery.tags.aesthetic')}</span>
                </div>
              </div>
              <div className={styles.galleryItem}>
                <img src="/assets/images/gallery/creator_4.png" alt="Retrato editorial de profesional creativo" />
                <div className={styles.galleryOverlay}>
                  <span className={styles.galleryTag}>{t('gallery.tags.pro')}</span>
                </div>
              </div>
            </div>

            {/* Photo 3 — wide */}
            <div className={styles.galleryItem} data-span="wide">
              <img src="/assets/images/gallery/creator_3.png" alt="Creador en ambiente de lujo lifestyle" />
              <div className={styles.galleryOverlay}>
                <span className={styles.galleryTag}>{t('gallery.tags.control')}</span>
              </div>
            </div>
          </div>

          <div className={styles.galleryFooter}>
            <p className={styles.galleryFooterText}>
              {t('gallery.footer')}
            </p>
            <Link to="/register" className={styles.galleryBtn}>
              {t('gallery.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* ====== CREDIBILITY SECTION ====== */}
      <section className={styles.credibilitySection}>
        <div className={styles.container}>
          <div className={styles.credibilityCard}>
            <div className={styles.credibilityHeader}>
              <h2 className={styles.credibilityTitle}>{t('credibility.title')}</h2>
              <p className={styles.credibilitySubtitle}>{t('credibility.subtitle')}</p>
            </div>
            <div className={styles.credibilityGrid}>
              {t('credibility.items', { returnObjects: true }).map((item, idx) => (
                <div key={idx} className={styles.credibilityItem}>
                  <div className={styles.credibilityBefore}>{item.before}</div>
                  <div className={styles.credibilityAfter}>{item.after}</div>
                </div>
              ))}
            </div>
            <div className={styles.credibilityFooter}>
              <span>{t('credibility.footer').split(' • ')[0]}</span>
              <span className={styles.dotSeparator}>•</span>
              <span>{t('credibility.footer').split(' • ')[1]}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SOCIAL PROOF / TESTIMONIALS ====== */}
      <section className={styles.socialSection}>
        <div className={styles.container}>
          <div className={styles.socialCard}>
            <div className={styles.socialEyebrow}>{t('social.eyebrow')}</div>
            <h2 className={styles.socialTitle}>{t('social.title')}</h2>
            <div className={styles.socialBadges}>
              {t('social.badges', { returnObjects: true }).map(item => (
                <span key={item} className={styles.socialBadge}>{item}</span>
              ))}
            </div>
            <div className={styles.testimonialGrid}>
              {t('social.testimonials', { returnObjects: true }).map(item => (
                <div key={item.quote} className={styles.testimonialCard}>
                  <div className={item.metric}>
                    <div className={styles.testimonialLabel}>
                      <span>{item.metric}</span>
                    </div>
                  </div>
                  <p className={styles.testimonialQuote}>"{item.quote}"</p>
                  <div className={styles.testimonialAuthor}>
                    <div className={styles.testimonialRole}>{item.role}</div>
                    <div className={styles.testimonialCompany}>{item.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== CLIENTS & PARTNERS ====== */}
      <TrustedBy />

      {/* ====== FINAL CTA ====== */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <div>
              <div className={styles.ctaBadge}>
                {t('cta_section.badge')}
              </div>
              <h2 className={styles.ctaTitle}>
                {t('cta_section.title')}
              </h2>
              <p className={styles.ctaDesc}>
                {t('cta_section.desc')}
              </p>
            </div>
            <div className={styles.ctaActions}>
              <Link to="/register" className={clsx(styles.ctaBtn, styles.ctaBtnPrimary)}>
                {t('cta_section.btn')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
